import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useBillingConfig } from "@/hooks/useBillingConfig";
import { formatPrice } from "@/utils/formatPrice";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, FileText, Printer, CheckCircle2, Send, XCircle } from "lucide-react";

// STATUS_MAP moved inside component to use t()

export default function AdminInvoices() {
  const { t } = useTranslation("admin");

  const STATUS_MAP: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    draft: { label: t("invoices.statusDraft"), variant: "secondary" },
    sent: { label: t("invoices.statusSent"), variant: "default" },
    paid: { label: t("invoices.statusPaid"), variant: "default" },
    overdue: { label: t("invoices.statusOverdue"), variant: "destructive" },
    cancelled: { label: t("invoices.statusCancelled"), variant: "outline" },
    credited: { label: t("invoices.statusCredited"), variant: "outline" },
  };

  const { organization } = useOrganization();
  const { config } = useBillingConfig();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInvoice, setSelectedInvoice] = useState<Record<string, unknown> | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["admin-invoices", organization?.id, statusFilter],
    queryFn: async () => {
      let q = supabase
        .from("invoices")
        .select("*, customers(full_name, email)")
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false });
      if (statusFilter !== "all") q = q.eq("status", statusFilter);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: invoiceLines = [] } = useQuery({
    queryKey: ["invoice-lines", selectedInvoice?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoice_lines")
        .select("*")
        .eq("invoice_id", selectedInvoice!.id)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!selectedInvoice?.id,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status, paid_at }: { id: string; status: string; paid_at?: string }) => {
      const updates: Record<string, unknown> = { status };
      if (paid_at) updates.paid_at = paid_at;
      if (status === "paid") updates.paid_amount = selectedInvoice?.total ?? 0;
      const { error } = await supabase.from("invoices").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      toast.success(t("admin:toast.invoiceStatusUpdated"));
      queryClient.invalidateQueries({ queryKey: ["admin-invoices"] });
      // G-019: mirror the REAL new status into the open detail sheet — the old
      // literal "updated" was not a valid enum value, so the badge fell back to
      // raw text and every status-gated action button vanished until reopen.
      setSelectedInvoice((prev) => prev ? { ...prev, status: variables.status, ...(variables.paid_at ? { paid_at: variables.paid_at } : {}) } : null);
    },
  });

  const handlePrint = (invoice: Record<string, unknown>) => {
    setSelectedInvoice(invoice);
    setDetailOpen(true);
    setTimeout(() => window.print(), 500);
  };

  // --- Subscription invoice bookings ---
  const [subInvoiceFilter, setSubInvoiceFilter] = useState("all");
  const [subInvoiceSearch, setSubInvoiceSearch] = useState("");
  const [selectedSubIds, setSelectedSubIds] = useState<Set<string>>(new Set());
  const { data: subInvoiceBookings = [], isLoading: subLoading } = useQuery({
    queryKey: ["sub-invoice-bookings", organization?.id, subInvoiceFilter],
    queryFn: async () => {
      let q = supabase
        .from("bookings")
        // G-101: explicit org filter via courts→halls (bookings has no
        // organization_id column) — defense-in-depth on top of RLS, matching
        // the invoices tab's .eq("organization_id", ...).
        .select("id, court_id, user_id, start_time, end_time, price_total, invoice_status, invoice_date, invoice_paid_date, courts!inner(name, halls!inner(organization_id)), profiles:user_id(full_name, email:raw_user_meta_data->>'email')")
        .eq("courts.halls.organization_id", organization!.id)
        .not("invoice_status", "is", null)
        .order("start_time", { ascending: false });
      if (subInvoiceFilter !== "all") q = q.eq("invoice_status", subInvoiceFilter);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!organization?.id,
  });

  const filteredSubBookings = subInvoiceSearch
    ? subInvoiceBookings.filter((b: any) => {
        const name = (b.profiles?.full_name ?? "").toLowerCase();
        return name.includes(subInvoiceSearch.toLowerCase());
      })
    : subInvoiceBookings;

  const bulkUpdateInvoiceStatus = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      const updates: Record<string, unknown> = { invoice_status: status, invoice_marked_by: null };
      if (status === "invoiced") updates.invoice_date = new Date().toISOString().slice(0, 10);
      if (status === "paid") updates.invoice_paid_date = new Date().toISOString().slice(0, 10);
      const { error } = await supabase.from("bookings").update(updates as any).in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-invoice-bookings"] });
      setSelectedSubIds(new Set());
      toast.success(t("invoices.statusUpdated"));
    },
  });

  const toggleSubId = (id: string) => {
    setSelectedSubIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const SUB_INVOICE_STATUS: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pending: { label: t("invoices.subStatusPending"), variant: "secondary" },
    invoiced: { label: t("invoices.subStatusInvoiced"), variant: "default" },
    paid: { label: t("invoices.statusPaid"), variant: "default" },
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="invoices">{t("invoices.tabInvoices")}</TabsTrigger>
          <TabsTrigger value="subscriptions">{t("invoices.tabSubscriptions")}</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-4 mt-4">

      <div className="flex gap-2 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value="draft">{t("invoices.statusDraft")}</SelectItem>
            <SelectItem value="sent">{t("invoices.filterSent")}</SelectItem>
            <SelectItem value="paid">{t("invoices.filterPaid")}</SelectItem>
            <SelectItem value="overdue">{t("invoices.filterOverdue")}</SelectItem>
            <SelectItem value="cancelled">{t("invoices.filterCancelled")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">{t("invoices.noInvoices")}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("invoices.invoiceNumber")}</TableHead>
                <TableHead>{t("invoices.customer")}</TableHead>
                <TableHead>{t("invoices.amount")}</TableHead>
                <TableHead>{t("invoices.status")}</TableHead>
                <TableHead>{t("invoices.dueDate")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => {
                const st = STATUS_MAP[inv.status] ?? { label: inv.status, variant: "secondary" as const };
                return (
                  <TableRow key={inv.id} className="cursor-pointer" onClick={() => { setSelectedInvoice(inv as unknown as Record<string, unknown>); setDetailOpen(true); }}>
                    <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                    <TableCell className="text-sm">{(inv.customers as Record<string, unknown> | null)?.full_name as string ?? "—"}</TableCell>
                    <TableCell className="text-sm font-medium">{formatPrice(inv.total)}</TableCell>
                    <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(inv.due_date), "d MMM yyyy", { locale: sv })}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handlePrint(inv); }}>
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

        </TabsContent>

        {/* Subscription invoices tab */}
        <TabsContent value="subscriptions" className="space-y-4 mt-4">
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={subInvoiceFilter} onValueChange={setSubInvoiceFilter}>
              <SelectTrigger className="w-40 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                <SelectItem value="pending">{t("invoices.subStatusPending")}</SelectItem>
                <SelectItem value="invoiced">{t("invoices.subFilterInvoiced")}</SelectItem>
                <SelectItem value="paid">{t("invoices.filterPaid")}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder={t("invoices.searchCustomer")}
              value={subInvoiceSearch}
              onChange={(e) => setSubInvoiceSearch(e.target.value)}
              className="h-9 w-48 text-sm"
            />
            {selectedSubIds.size > 0 && (
              <div className="flex gap-2 ml-auto">
                <Button size="sm" variant="outline" onClick={() => bulkUpdateInvoiceStatus.mutate({ ids: [...selectedSubIds], status: "invoiced" })}>
                  <Send className="h-3.5 w-3.5 mr-1" /> {t("invoices.markInvoiced")} ({selectedSubIds.size})
                </Button>
                <Button size="sm" onClick={() => bulkUpdateInvoiceStatus.mutate({ ids: [...selectedSubIds], status: "paid" })}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("invoices.markPaid")} ({selectedSubIds.size})
                </Button>
              </div>
            )}
          </div>

          {subLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : filteredSubBookings.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">{t("invoices.noSubscriptionInvoices")}</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox
                        checked={selectedSubIds.size === filteredSubBookings.length && filteredSubBookings.length > 0}
                        onCheckedChange={(v) => {
                          if (v) setSelectedSubIds(new Set(filteredSubBookings.map((b: any) => b.id)));
                          else setSelectedSubIds(new Set());
                        }}
                      />
                    </TableHead>
                    <TableHead>{t("invoices.customer")}</TableHead>
                    <TableHead>{t("invoices.court")}</TableHead>
                    <TableHead>{t("invoices.dateTime")}</TableHead>
                    <TableHead>{t("invoices.amount")}</TableHead>
                    <TableHead>{t("invoices.status")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubBookings.map((b: any) => {
                    const st = SUB_INVOICE_STATUS[b.invoice_status] ?? { label: b.invoice_status, variant: "secondary" as const };
                    return (
                      <TableRow key={b.id}>
                        <TableCell>
                          <Checkbox checked={selectedSubIds.has(b.id)} onCheckedChange={() => toggleSubId(b.id)} />
                        </TableCell>
                        <TableCell className="text-sm">{b.profiles?.full_name ?? "—"}</TableCell>
                        <TableCell className="text-sm">{b.courts?.name ?? "—"}</TableCell>
                        <TableCell className="text-sm">
                          {format(new Date(b.start_time), "d MMM", { locale: sv })} {format(new Date(b.start_time), "HH:mm")}–{format(new Date(b.end_time), "HH:mm")}
                        </TableCell>
                        <TableCell className="text-sm font-medium">{formatPrice(b.price_total ?? 0)}</TableCell>
                        <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invoice Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-[500px] sm:w-[540px] p-0">
          <SheetHeader className="px-5 pt-5 pb-3">
            <SheetTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {selectedInvoice?.invoice_number}
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-180px)] px-5">
            {selectedInvoice && (
              <div className="space-y-4 pb-6 print-invoice">
                {/* Print header */}
                <div className="hidden print:block mb-6">
                  <h1 className="text-2xl font-bold">{config.company_name || t("invoices.company")}</h1>
                  <p className="text-sm">{config.company_org_number}</p>
                  <p className="text-sm">{config.company_address}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">{t("invoices.customer")}</p>
                    <p className="font-medium">{(selectedInvoice.customers as Record<string, unknown> | null)?.full_name ?? "—"}</p>
                    <p className="text-xs text-muted-foreground">{(selectedInvoice.customers as Record<string, unknown> | null)?.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-muted-foreground text-xs">{t("invoices.status")}</p>
                    <Badge variant={STATUS_MAP[selectedInvoice.status]?.variant ?? "secondary"}>
                      {STATUS_MAP[selectedInvoice.status]?.label ?? selectedInvoice.status}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">{t("invoices.issueDate")}</p>
                    <p>{format(new Date(selectedInvoice.issue_date), "d MMM yyyy", { locale: sv })}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">{t("invoices.dueDate")}</p>
                    <p>{format(new Date(selectedInvoice.due_date), "d MMM yyyy", { locale: sv })}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">{t("invoices.reference")}</p>
                    <p>{selectedInvoice.reference || "—"}</p>
                  </div>
                </div>

                <Separator />

                {/* Lines */}
                <div>
                  <p className="text-xs font-semibold mb-2">{t("invoices.lines")}</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground text-xs">
                        <th className="pb-1">{t("invoices.description")}</th>
                        <th className="pb-1 text-right">{t("invoices.quantity")}</th>
                        <th className="pb-1 text-right">{t("invoices.unitPrice")}</th>
                        <th className="pb-1 text-right">{t("invoices.amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceLines.map((line) => (
                        <tr key={line.id} className="border-b last:border-0">
                          <td className="py-1.5">{line.description}</td>
                          <td className="py-1.5 text-right">{line.quantity}</td>
                          <td className="py-1.5 text-right">{formatPrice(line.unit_price, { showCurrency: false })}</td>
                          <td className="py-1.5 text-right font-medium">{formatPrice(line.total, { showCurrency: false })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Separator />

                {/* Totals */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("invoices.net")}</span>
                    <span>{formatPrice(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("invoices.vat")} ({selectedInvoice.vat_rate}%)</span>
                    <span>{formatPrice(selectedInvoice.vat_amount)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base">
                    <span>{t("invoices.total")}</span>
                    <span>{formatPrice(selectedInvoice.total)}</span>
                  </div>
                </div>

                {/* Payment info */}
                {(config.company_bankgiro || config.company_swish) && (
                  <>
                    <Separator />
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p className="font-medium text-foreground">{t("invoices.paymentInfo")}</p>
                      {config.company_bankgiro && <p>Bankgiro: {config.company_bankgiro}</p>}
                      {config.company_swish && <p>Swish: {config.company_swish}</p>}
                      <p>{t("invoices.useInvoiceAsReference", { number: selectedInvoice.invoice_number })}</p>
                    </div>
                  </>
                )}

                {selectedInvoice.notes && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs font-medium mb-1">{t("invoices.notes")}</p>
                      <p className="text-sm text-muted-foreground">{selectedInvoice.notes}</p>
                    </div>
                  </>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Actions */}
          {selectedInvoice && (
            <div className="border-t px-5 py-3 flex gap-2 flex-wrap">
              {selectedInvoice.status === "draft" && (
                <Button size="sm" variant="outline" onClick={() => updateStatus.mutate({ id: selectedInvoice.id, status: "sent" })}>
                  <Send className="h-3.5 w-3.5 mr-1" /> {t("invoices.markSent")}
                </Button>
              )}
              {["draft", "sent", "overdue"].includes(selectedInvoice.status) && (
                <Button size="sm" onClick={() => updateStatus.mutate({ id: selectedInvoice.id, status: "paid", paid_at: new Date().toISOString() })}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("invoices.markAsPaid")}
                </Button>
              )}
              {["draft", "sent"].includes(selectedInvoice.status) && (
                <Button size="sm" variant="destructive" onClick={() => updateStatus.mutate({ id: selectedInvoice.id, status: "cancelled" })}>
                  <XCircle className="h-3.5 w-3.5 mr-1" /> {t("invoices.cancel")}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => window.print()}>
                <Printer className="h-3.5 w-3.5 mr-1" /> {t("invoices.print")}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
